import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { IonModal } from '@ionic/angular';
import { Group, Vehicule } from 'src/app/model/data-managemnt';
import { DataManagementService } from 'src/app/pages/data-management/data-management.service';

/**
 * Guided single-input search flow: Group -> Vehicle -> Start date -> End date.
 * Only one step is shown at a time; completed steps collapse into editable chips.
 *
 * Exposes `startDate` / `endDate` (ISO strings) so it is a drop-in replacement
 * for the previous CalendarModalComponent that the modals read via @ViewChild.
 */
@Component({
  selector: 'app-search-stepper',
  templateUrl: './search-stepper.component.html',
  styleUrls: ['./search-stepper.component.scss'],
})
export class SearchStepperComponent implements OnInit, OnDestroy {
  @ViewChild('startDateModal') startDateModal?: IonModal;
  @ViewChild('endDateModal') endDateModal?: IonModal;

  @Input() groups: Group[] = [];

  @Input() selectedGroup: Group | null = null;
  @Output() selectedGroupChange = new EventEmitter<Group | null>();

  @Input() selectedVehicule: Vehicule | null = null;
  @Output() selectedVehiculeChange = new EventEmitter<Vehicule | null>();

  @Input() start: Date;
  @Input() end: Date;

  @Output() validate = new EventEmitter<void>();

  // Mirrors CalendarModalComponent so parent submit methods keep working.
  startDate: string;
  endDate: string;

  // 0 = group, 1 = vehicle, 2 = start date, 3 = end date
  step = 0;

  private startModalTimer: ReturnType<typeof setTimeout> | null = null;
  private endModalTimer: ReturnType<typeof setTimeout> | null = null;
  private submitted = false;

  constructor(public dataManagementService: DataManagementService) {}

  ngOnInit() {
    this.startDate = (this.start ? new Date(this.start) : new Date()).toISOString();
    this.endDate = (this.end ? new Date(this.end) : new Date()).toISOString();
    this.step = 0;
  }

  ngOnDestroy() {
    this.clearModalTimers();
  }

  get visibleGroups(): Group[] {
    return (this.groups || []).filter((g) => !g.reportIsInvisible);
  }

  get vehicules(): Vehicule[] {
    return this.selectedGroup?.vehicules || [];
  }

  vehName(v: Vehicule): string {
    return this.dataManagementService.getVehiculeName(v);
  }

  onGroup(group: Group) {
    this.selectedGroup = group;
    this.selectedGroupChange.emit(group);
    this.selectedVehicule = null;
    this.selectedVehiculeChange.emit(null);
    this.step = 1;
  }

  onVehicule(vehicule: Vehicule) {
    this.selectedVehicule = vehicule;
    this.selectedVehiculeChange.emit(vehicule);
    this.step = 2;
    this.scheduleStartDateModal();
  }

  private clearModalTimers() {
    if (this.startModalTimer) {
      clearTimeout(this.startModalTimer);
      this.startModalTimer = null;
    }
    if (this.endModalTimer) {
      clearTimeout(this.endModalTimer);
      this.endModalTimer = null;
    }
  }

  private scheduleStartDateModal() {
    this.clearModalTimers();
    this.startModalTimer = setTimeout(() => {
      this.startModalTimer = null;
      if (this.step === 2 && !this.submitted) {
        void this.startDateModal?.present();
      }
    }, 150);
  }

  private scheduleEndDateModal() {
    this.clearModalTimers();
    this.endModalTimer = setTimeout(() => {
      this.endModalTimer = null;
      if (this.step === 3 && !this.submitted) {
        void this.endDateModal?.present();
      }
    }, 150);
  }

  /** Keep start/end values in sync while the picker is open. Do not advance steps here. */
  onStartDateChange(event: CustomEvent) {
    const value = event.detail?.value as string | undefined;
    if (!value) {
      return;
    }
    this.startDate = value;
    if (!this.endDate || new Date(this.endDate) < new Date(value)) {
      this.endDate = value;
    }
  }

  /**
   * Advance after the start-date modal closes.
   * ionChange alone is unreliable (Done/dismiss race), which left users stuck on step 3.
   */
  onStartDateModalDismiss() {
    if (this.submitted) {
      return;
    }
    if (this.step === 2) {
      this.goToEndDateStep();
    }
  }

  goToEndDateStep() {
    if (this.step !== 2 || this.submitted) {
      return;
    }
    if (!this.endDate || new Date(this.endDate) < new Date(this.startDate)) {
      this.endDate = this.startDate;
    }
    this.step = 3;
    void this.startDateModal?.dismiss().catch(() => undefined);
    this.scheduleEndDateModal();
  }

  prev() {
    if (this.step > 0) {
      this.clearModalTimers();
      this.step--;
    }
  }

  async submit() {
    if (!this.selectedVehicule || this.submitted) {
      return;
    }
    this.submitted = true;
    this.clearModalTimers();
    await Promise.all([
      this.startDateModal?.dismiss().catch(() => undefined),
      this.endDateModal?.dismiss().catch(() => undefined),
    ]);
    // Defer emit so nested modal dismiss does not click-through to the gear
    // button and reopen "Paramètres de Recherche" (first-load race).
    setTimeout(() => this.validate.emit(), 120);
  }

  /** Confirm end date from its picker, then submit the whole search. */
  async confirmEndDateAndSubmit() {
    if (this.submitted) {
      return;
    }
    await this.endDateModal?.dismiss().catch(() => undefined);
    await this.submit();
  }
}
